import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as apiUtil from "../../../util/apiUtil";
import { aesEncrypt } from "../../../util/aes256";

const NAVER_OAUTH_BASE = "https://nid.naver.com/oauth2.0/authorize";

export default function NaverLandingPage() {
  const { shortUrl } = useParams<{ shortUrl: string }>();
  const [clientId, setClientId] = useState<string | null>(null);
  const [appUid, setAppUid] = useState<string | null>(null);
  const [cmpnId, setCmpnId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const landingLogged = useRef(false);

  useEffect(() => {
    if (!shortUrl) return;

    // 앱 OAuth 정보 조회
    apiUtil.asyncRequest({
      url: `/api/bizchat/naver-app/public/oauth-info?shortUrl=${shortUrl}`,
      method: apiUtil.requestMethod.GET,
      success: (result: any) => {
        const resolvedCmpnId = String(result.cmpnId);
        setCmpnId(resolvedCmpnId);
        setAppUid(String(result.appUid));
        setClientId(result.clientId);
        setLoading(false);

        // LANDING 퍼널 로그 (cmpnId 확보 후 최초 1회)
        if (!landingLogged.current) {
          landingLogged.current = true;
          if (!window.sessionStorage.getItem("naverLoginSessionId")) {
            window.sessionStorage.setItem("naverLoginSessionId", crypto.randomUUID());
          }
          const sessionId = window.sessionStorage.getItem("naverLoginSessionId");
          apiUtil.asyncRequest({
            url: "/api/common/naver/funnel",
            method: apiUtil.requestMethod.POST,
            data: { cmpnId: resolvedCmpnId, eventType: "LANDING", sessionId },
          });
        }
      },
      fail: () => {
        setError(true);
        setLoading(false);
      },
    });
  }, [shortUrl]);

  const handleNaverLogin = () => {
    if (!clientId || !appUid) return;

    // LOGIN_CLICK 퍼널 로그
    const sessionId = window.sessionStorage.getItem("naverLoginSessionId");
    apiUtil.asyncRequest({
      url: "/api/common/naver/funnel",
      method: apiUtil.requestMethod.POST,
      data: { cmpnId, eventType: "LOGIN_CLICK", sessionId },
    });

    // 네이버 OAuth 리다이렉트
    const redirectUri = `${window.location.origin}/naver/${appUid}/callback`;
    const state = encodeURIComponent(aesEncrypt(JSON.stringify({ cmpnId })));
    const oauthUrl = `${NAVER_OAUTH_BASE}?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
    window.location.href = oauthUrl;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p style={styles.errorText}>유효하지 않은 링크입니다.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* 상단 안내 */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>안내 페이지</h1>
          <p style={styles.headerSubtitle}>메시지를 통해 안내된 내용입니다</p>
        </div>

        {/* 메시지 설명 */}
        <div style={styles.body}>
          <h2 style={styles.bodyTitle}>간편 참여 안내</h2>
          <p style={styles.bodyDesc}>
            문자 메시지에서 안내된 이벤트 또는 혜택에<br />
            간편하게 참여하실 수 있습니다.
          </p>
        </div>

        {/* 설명 박스 */}
        <div style={styles.infoBox}>
          <ul style={styles.infoList}>
            <li style={styles.infoItem}>✔ 네이버 간편 로그인으로 빠르게 참여</li>
            <li style={styles.infoItem}>✔ 참여 후 상세 안내 확인</li>
          </ul>
        </div>

        {/* 네이버 로그인 버튼 (공식 BI 가이드 기준) */}
        <button style={styles.naverBtn} onClick={handleNaverLogin}>
          <span style={styles.naverIconWrap}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
              <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z" />
            </svg>
          </span>
          <span style={styles.naverBtnText}>네이버 로그인</span>
        </button>

        {/* 하단 안내 */}
        <p style={styles.disclaimer}>
          안내 목적 외 다른 용도로 사용되지 않습니다.
        </p>

      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    padding: "16px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "32px 24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "0px",
    width: "100%",
    maxWidth: "380px",
  },
  header: {
    textAlign: "center",
    marginBottom: "24px",
  },
  headerTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1a1a1a",
    margin: "0 0 4px 0",
  },
  headerSubtitle: {
    fontSize: "13px",
    color: "#888",
    margin: 0,
  },
  body: {
    textAlign: "center",
    marginBottom: "20px",
  },
  bodyTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0 0 12px 0",
  },
  bodyDesc: {
    fontSize: "14px",
    color: "#555",
    lineHeight: "1.6",
    margin: 0,
  },
  infoBox: {
    backgroundColor: "#f9f9f9",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px",
  },
  infoList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  infoItem: {
    fontSize: "14px",
    color: "#555",
  },
  naverBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    backgroundColor: "#03A94D",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "0 20px",
    width: "100%",
    height: "45px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "-0.3px",
  },
  naverIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    lineHeight: 0,
  },
  naverBtnText: {
    textAlign: "center",
  },
  disclaimer: {
    fontSize: "12px",
    color: "#aaa",
    textAlign: "center",
    marginTop: "16px",
    lineHeight: "1.5",
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid #e0e0e0",
    borderTopColor: "#03A94D",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  errorText: {
    color: "#888",
    fontSize: "15px",
  },
};
